import { Accordion, AccordionDetails, Card, CardContent, Grid, IconButton, Stack, Typography } from '@mui/material'
import { AccordionTitle } from 'components/styled/Accordions'
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import i18n from 'simple-react-i18n'
import { keys, compact, groupBy, isNil } from 'lodash'
import { DOCUMENT, PICTURE, PICTURES_EXTENSIONS } from 'components/file/constant/FileConstants'
import { getDate, getYearOrString } from 'utils/DateUtil'
import { getI18nTitleDataLength } from 'utils/StringUtil'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import { getFileExtension, getFileInfo, getFileName, getFileNatureLabelTaxon } from 'utils/FileUtils'
import useSandreList from 'utils/customHook/useSandreList'
import Fancybox from 'components/fancybox/Fancybox'
import { Cancel, CheckBox, CheckBoxOutlineBlank, Edit } from '@mui/icons-material'
import ConfirmModal from 'components/modal/ConfirmModal'
import ReferencialAction from 'referencial/action/ReferencialAction'
import { getYear } from 'date-fns'
import moment from 'moment'
import MessageCard from 'components/card/MessageCard'
import UpdateFileDialog from './UpdateFileDialog'
import ReferencialFileAction from './actions/ReferencialFileAction'
import ApplicationConf from 'conf/ApplicationConf'

const DATE = 'date'
const FILE = 'file'
const ACTIVE = 'active'

const groupFiles = (files, group, typePictures, typeDocuments) => {
    switch (group) {
        case FILE:
            return groupBy(files, f => {
                const listTypes = f.fileType === PICTURE ? typePictures : typeDocuments
                return listTypes.find(t => t.mnemonique === f.type || t.name === f.type)?.name
            })
        case ACTIVE:
            return groupBy(files, file => file.active === '1' ? i18n.active : i18n.inactive)
        case DATE:
        default:
            return groupBy(files, file => getYearOrString(file.date, i18n.thisYear, ''))
    }
}

const FileCard = ({
    file = {},
    isEdit = false,
    code = '',

    onUpdate,
    onDelete,
}) => {
    const displayName = file.isAllDataName && code === file.code ? file.shortName : getFileName(file.name)

    const {
        sandreConstant,
        image,
    } = getFileInfo(file)

    const fileNatures = useSandreList(sandreConstant)

    return (
        <Fancybox>
            <a
                href={isEdit ? undefined : file.url}
                target='_blank'
                data-fancybox={file.fileType == PICTURE && !isEdit ? 'images' : undefined}
                data-caption={displayName}
            >
                <Card>
                    <CardContent>
                        <Grid container spacing={2} alignItems='center'>
                            <Grid size={3} textAlign='center'>
                                {image}
                            </Grid>
                            <Grid container size={9} rowSpacing={0} columnSpacing={0.2}>
                                <Grid size='grow'>
                                    <Typography
                                        style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                        fontWeight='bold'
                                        variant='subtitle1'
                                        color='text.secondary'
                                        component='div'
                                    >
                                        {displayName}
                                    </Typography>
                                </Grid>
                                {isEdit && (
                                    <>
                                        <Grid size='auto'>
                                            <IconButton
                                                color='primary'
                                                size='small'
                                                onClick={() => onUpdate(file.name)}
                                            >
                                                <Edit />
                                            </IconButton>
                                        </Grid>
                                        <Grid size='auto'>
                                            <IconButton
                                                color='primary'
                                                size='small'
                                                onClick={() => onDelete(file.name)}
                                            >
                                                <Cancel />
                                            </IconButton>
                                        </Grid>
                                    </>
                                )}
                                <Stack width={'100%'}>
                                    <Typography variant='subtitle1' color='text.secondary'>
                                        {i18n.type} : {getFileExtension(file.name)}
                                    </Typography>

                                    <Typography variant='subtitle1' color='text.secondary'>
                                        {i18n.nature} : {getFileNatureLabelTaxon(file, fileNatures)}
                                    </Typography>

                                    <Typography variant='subtitle1' color='text.secondary'>
                                        {i18n.date} : {getDate(file.date)}
                                    </Typography>
                                </Stack>
                                <Grid size={6} display='flex' alignItems='center'>
                                    <Typography variant='subtitle1' color='text.secondary'>
                                        {i18n.active} :
                                    </Typography>
                                    {file.active === '1' ? <CheckBox /> : <CheckBoxOutlineBlank />}
                                </Grid>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </a>
        </Fancybox>
    )
}

FileCard.propTypes = {
    file: PropTypes.shape({}),
    isEdit: PropTypes.bool,
    code: PropTypes.string,
    onUpdate: PropTypes.func,
    onDelete: PropTypes.func,
}

const FileAccordion = ({
    data = [],
    group,
    filter,
    isEdit = false,
    refetchFiles,
    type,
}) => {
    const [selectedFile, setSelectedFile] = useState()
    const [isConfirmOpen, setIsConfirmOpen] = useState(false)
    const [isUpdateFileOpen, setIsUpdateFileOpen] = useState(false)

    const dispatch = useDispatch()

    const hasCode = data.length > 0


    useEffect(() => {
        dispatch(ReferencialAction.fetchSandreCodes())
    }, [dispatch])


    const typePictures = useSandreList(SANDRE.TYPE_PHOTO)
    const typeDocuments = useSandreList(SANDRE.TYPE_DOCUMENT)


    const formattedFiles = (data || []).map(doc => {
        const extension = `.${getFileExtension(doc.name)?.toLowerCase()}`

        return {
            ...doc,
            url: ApplicationConf.picture.filePicture(doc.name, doc.path),
            name: doc.name,
            date: doc.dateMaj,
            active: doc.actif ? '1' : '0',
            Nature: doc.nature,
            fileType: PICTURES_EXTENSIONS.includes(extension) ? PICTURE : DOCUMENT,
        }
    })


    const filteredFiles = useMemo(() => {
        const {
            searchValue = '',
            extension,
            naturePicture,
            natureDocument,
            year,
            active,
        } = filter || {}

        const search = searchValue.trim().toLowerCase()
        const typePicture = typePictures.find(t => t.mnemonique === naturePicture || t.name === naturePicture)
        const typeDocument = typeDocuments.find(t => t.mnemonique === natureDocument || t.name === natureDocument)

        return formattedFiles.filter(f => {
            if (search && !f.name?.toLowerCase().includes(search)) {
                return false
            }

            if (!isNil(active) && active !== '') {
                if (f.active !== active) return false
            }

            if (year) {
                const fileYear = getYear(moment(f.dateMaj))
                if (fileYear !== year) return false
            }

            if (extension) {
                const ext = `.${getFileExtension(f.name)?.toLowerCase()}`
                if (ext !== extension.toLowerCase()) return false
            }

            if (naturePicture && f.fileType === PICTURE) {
                const match = typePicture && typePicture.code === f.nature
                if (!match) return false
            }

            if (natureDocument && f.fileType === DOCUMENT) {
                const match = typeDocument && typeDocument.code === f.nature
                if (!match) return false
            }

            return true
        })
    }, [filter, formattedFiles, typePictures, typeDocuments])


    const groups = groupFiles(
        filteredFiles,
        group,
        typePictures,
        typeDocuments,
    )

    return (
        <Grid size={12}>
            {hasCode ? (
                <>
                    <Stack spacing={1}>
                        {keys(groups).reverse().map((key, i) => {
                            const fileCards = compact(groups[key].map(f => {
                                return (
                                    <Grid key={f.name} size={3}>
                                        <FileCard
                                            file={f}
                                            isEdit={isEdit}
                                            code={f.code}
                                            onUpdate={name => {
                                                setSelectedFile(name)
                                                setIsUpdateFileOpen(true)
                                            }}
                                            onDelete={name => {
                                                setSelectedFile(name)
                                                setIsConfirmOpen(true)
                                            }}
                                        />
                                    </Grid>
                                )
                            }))

                            if (!fileCards.length) return undefined

                            return (
                                <Grid key={key} size={12}>
                                    <Accordion defaultExpanded={i === 0}>
                                        <AccordionTitle
                                            title={`${key} (${fileCards.length} ${getI18nTitleDataLength(i18n.element, i18n.elements, fileCards.length)})`}
                                        />
                                        <AccordionDetails>
                                            <Grid container spacing={1}>
                                                {fileCards}
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                            )
                        })}
                    </Stack>
                    <UpdateFileDialog
                        isOpen={isUpdateFileOpen}
                        onClose={() => {
                            setIsUpdateFileOpen(false)
                            refetchFiles()
                        }}
                        fileName={selectedFile}
                        files={data}
                        type={type}
                    />
                    <ConfirmModal
                        isOpen={isConfirmOpen}
                        title={i18n.deletingConfirmation}
                        subTitle={selectedFile}
                        onClose={() => {
                            setIsConfirmOpen(false)
                            refetchFiles()
                        }}
                        onValidate={() => {
                            const file = data.find(f => f.name === selectedFile)
                            dispatch(ReferencialFileAction.deleteFile(file.id.toString(), file.name, file.path)).then(() => {
                                setIsConfirmOpen(false)
                                refetchFiles()
                            })
                        }}
                    />
                </>
            ) : (<MessageCard>{i18n.noFileOrPicture}</MessageCard>)}
        </Grid>
    )
}

FileAccordion.propTypes = {
    data: PropTypes.any,
    group: PropTypes.string,
    filter: PropTypes.shape({
        searchValue: PropTypes.string,
        extension: PropTypes.string,
        naturePicture: PropTypes.string,
        natureDocument: PropTypes.string,
        year: PropTypes.string,
        active: PropTypes.string,
    }),
    isEdit: PropTypes.bool,
    refetchFiles: PropTypes.func,
    type: PropTypes.string,
}

export default FileAccordion